import React from 'react';
import PropTypes from 'prop-types';
import { Form, Input, Select, DatePicker, Button } from 'antd';
import {
	UploadOutlined,
	EyeOutlined,
	CloseOutlined,
	ExclamationCircleOutlined,
} from '@ant-design/icons';
import { Text } from 'atoms';
import { OWNERSHIP_TYPE_OPTIONS } from 'componentsv2/forms/vendor-steps/VehicleInformationStep.constants';
import {
	VALID_TILL_DATE_MESSAGE,
	VEHICLE_NUMBER_PATTERN,
	VEHICLE_NUMBER_MESSAGE,
} from '../create-vehiclev2/CreateVehiclePageV2.constants';
import {
	StyledForm,
	FormGrid,
	InlineRow,
	UploadButtonWrapper,
	DatePickerWrapper,
	FileDisplayContainer,
	FileInfo,
	FileIcon,
	RemoveFileButton,
	HiddenFileInput,
	PrimaryBrowseFileButton,
	PendingDocumentStatus,
	ActionButton,
	FormActions,
} from './VehicleDetailsPageV2.styles';

const { Option } = Select;

// Constants
const UPLOAD_DOCUMENT_MESSAGE = 'Please upload document';

function VehicleDetailsForm({
	form,
	isEditMode,
	loading,
	ownershipType,
	vendorOptions,
	vehicleTypeOptions,
	uploading,
	fileKeys,
	fileNames,
	// Refs
	rcDocFileInputRef,
	insuranceDocFileInputRef,
	pucDocFileInputRef,
	consentFileInputRef,
	// Handlers
	handleBrowseFile,
	handleFileChange,
	handleViewDocument,
	handleRemoveFile,
	handleToggleEdit,
	handleSubmit,
	skipDocumentUpload,
}) {
	const gpsInstalled = Form.useWatch('gps_installed', form);
	const isPucExpiryDateRequired = !skipDocumentUpload && Boolean(fileKeys.puc_doc_key);

	return (
		<StyledForm form={form} layout="vertical">
			<FormGrid>
				{/* Row 1: Select Vendor | Vehicle Number */}
				<Form.Item
					name="vendor_id"
					label="Select Vendor"
					rules={[{ required: true, message: 'Please select vendor' }]}
				>
					<Select
						placeholder="Select"
						showSearch
						disabled={!isEditMode}
						filterOption={(input, option) =>
							(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
						}
					>
						{vendorOptions.map((vendor) => (
							<Option key={vendor.value} value={vendor.value}>
								{vendor.label}
							</Option>
						))}
					</Select>
				</Form.Item>
				<Form.Item
					name="vehicle_number"
					label="Vehicle Number"
					rules={[
						{ required: true, message: 'Please enter vehicle number' },
						{
							pattern: VEHICLE_NUMBER_PATTERN,
							message: VEHICLE_NUMBER_MESSAGE,
						},
					]}
				>
					<Input
						placeholder="Eg : DL01AB1234 or DL 01 AB 1234"
						disabled={!isEditMode}
						onChange={(e) =>
							form.setFieldsValue({
								vehicle_number: e.target.value.toUpperCase(),
							})
						}
						style={{ textTransform: 'uppercase' }}
					/>
				</Form.Item>

				{/* Row 2: Vehicle Type | Insurance Doc Upload + Valid Till */}
				<Form.Item
					name="vehicle_type_id"
					label="Vehicle Type"
					rules={[{ required: true, message: 'Please select vehicle type' }]}
				>
					<Select
						placeholder="Select"
						showSearch
						disabled={!isEditMode}
						filterOption={(input, option) =>
							(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
						}
					>
						{vehicleTypeOptions.map((type) => (
							<Option key={type.value} value={type.value}>
								{type.label}
							</Option>
						))}
					</Select>
				</Form.Item>
				<Form.Item
					name="insurance_key"
					label="Insurance Doc Upload"
					rules={[
						{
							required: !skipDocumentUpload,
							message: `${UPLOAD_DOCUMENT_MESSAGE} insurance`,
						},
					]}
				>
					<InlineRow>
						<UploadButtonWrapper>
							{!isEditMode && !fileNames.insurance && skipDocumentUpload ? (
								<PendingDocumentStatus>
									<ExclamationCircleOutlined style={{ fontSize: 14 }} />
									<Text
										style={{ color: 'inherit', fontSize: 12, fontWeight: 600 }}
									>
										Pending
									</Text>
								</PendingDocumentStatus>
							) : (
								<PrimaryBrowseFileButton
									icon={<UploadOutlined />}
									onClick={() =>
										handleBrowseFile('insurance', insuranceDocFileInputRef)
									}
									loading={uploading.insurance}
									disabled={!isEditMode || skipDocumentUpload}
								>
									Browse File
								</PrimaryBrowseFileButton>
							)}
							{fileNames.insurance && (
								<FileDisplayContainer>
									<FileInfo>
										<FileIcon />
										<Text
											style={{
												fontSize: 14,
												color: '#3b82f6',
												cursor: 'pointer',
												flex: 1,
												minWidth: 0,
											}}
											onClick={() =>
												handleViewDocument(
													fileKeys.insurance_key,
													'Insurance Document'
												)
											}
										>
											{fileNames.insurance}
										</Text>
										<EyeOutlined
											style={{
												fontSize: 16,
												color: '#3b82f6',
												cursor: 'pointer',
												flexShrink: 0,
											}}
											onClick={() =>
												handleViewDocument(
													fileKeys.insurance_key,
													'Insurance Document'
												)
											}
										/>
									</FileInfo>
									{isEditMode && (
										<RemoveFileButton
											onClick={() => handleRemoveFile('insurance')}
										>
											<CloseOutlined />
										</RemoveFileButton>
									)}
								</FileDisplayContainer>
							)}
							<HiddenFileInput
								type="file"
								ref={insuranceDocFileInputRef}
								accept=".pdf,.png,.jpg,.jpeg"
								onChange={(e) => handleFileChange(e, 'insurance')}
							/>
						</UploadButtonWrapper>
						<DatePickerWrapper>
							<Form.Item
								name="insurance_validity_date"
								label="Valid Till"
								rules={[
									{
										required: !skipDocumentUpload,
										message: VALID_TILL_DATE_MESSAGE,
									},
								]}
								style={{ marginBottom: 0 }}
							>
								<DatePicker
									style={{ width: '100%' }}
									placeholder="Eg : 12-2-2025"
									format="DD-MM-YYYY"
									disabled={!isEditMode || skipDocumentUpload}
								/>
							</Form.Item>
						</DatePickerWrapper>
					</InlineRow>
				</Form.Item>

				{/* Row 3: Vehicle RC Doc Upload + Valid Till | PUC Doc Upload + Valid Till */}
				<Form.Item
					name="rc_doc_key"
					label="Vehicle RC Doc Upload"
					rules={[
						{ required: !skipDocumentUpload, message: `${UPLOAD_DOCUMENT_MESSAGE} RC` },
					]}
				>
					<InlineRow>
						<UploadButtonWrapper>
							{!isEditMode && !fileNames.rc_doc && skipDocumentUpload ? (
								<PendingDocumentStatus>
									<ExclamationCircleOutlined style={{ fontSize: 14 }} />
									<Text
										style={{ color: 'inherit', fontSize: 12, fontWeight: 600 }}
									>
										Pending
									</Text>
								</PendingDocumentStatus>
							) : (
								<PrimaryBrowseFileButton
									icon={<UploadOutlined />}
									onClick={() => handleBrowseFile('rc_doc', rcDocFileInputRef)}
									loading={uploading.rc_doc}
									disabled={!isEditMode || skipDocumentUpload}
								>
									Browse File
								</PrimaryBrowseFileButton>
							)}
							{fileNames.rc_doc && (
								<FileDisplayContainer>
									<FileInfo>
										<FileIcon />
										<Text
											style={{
												fontSize: 14,
												color: '#3b82f6',
												cursor: 'pointer',
												flex: 1,
												minWidth: 0,
											}}
											onClick={() =>
												handleViewDocument(
													fileKeys.rc_doc_key,
													'RC Document'
												)
											}
										>
											{fileNames.rc_doc}
										</Text>
										<EyeOutlined
											style={{
												fontSize: 16,
												color: '#3b82f6',
												cursor: 'pointer',
												flexShrink: 0,
											}}
											onClick={() =>
												handleViewDocument(
													fileKeys.rc_doc_key,
													'RC Document'
												)
											}
										/>
									</FileInfo>
									{isEditMode && (
										<RemoveFileButton
											onClick={() => handleRemoveFile('rc_doc')}
										>
											<CloseOutlined />
										</RemoveFileButton>
									)}
								</FileDisplayContainer>
							)}
							<HiddenFileInput
								type="file"
								ref={rcDocFileInputRef}
								accept=".pdf,.png,.jpg,.jpeg"
								onChange={(e) => handleFileChange(e, 'rc_doc')}
							/>
						</UploadButtonWrapper>
						<DatePickerWrapper>
							<Form.Item
								name="rc_expiry_date"
								label="Valid Till"
								rules={[
									{
										required: !skipDocumentUpload,
										message: VALID_TILL_DATE_MESSAGE,
									},
								]}
								style={{ marginBottom: 0 }}
							>
								<DatePicker
									style={{ width: '100%' }}
									placeholder="Eg : 12-2-2025"
									format="DD-MM-YYYY"
									disabled={!isEditMode || skipDocumentUpload}
								/>
							</Form.Item>
						</DatePickerWrapper>
					</InlineRow>
				</Form.Item>
				<Form.Item name="puc_doc_key" label="PUC Doc Upload">
					<InlineRow>
						<UploadButtonWrapper>
							<PrimaryBrowseFileButton
								icon={<UploadOutlined />}
								onClick={() => handleBrowseFile('puc_doc', pucDocFileInputRef)}
								loading={uploading.puc_doc}
								disabled={!isEditMode || skipDocumentUpload}
							>
								Browse File
							</PrimaryBrowseFileButton>
							{fileNames.puc_doc && (
								<FileDisplayContainer>
									<FileInfo>
										<FileIcon />
										<Text
											style={{
												fontSize: 14,
												color: '#3b82f6',
												cursor: 'pointer',
												flex: 1,
												minWidth: 0,
											}}
											onClick={() =>
												handleViewDocument(
													fileKeys.puc_doc_key,
													'PUC Document'
												)
											}
										>
											{fileNames.puc_doc}
										</Text>
										<EyeOutlined
											style={{
												fontSize: 16,
												color: '#3b82f6',
												cursor: 'pointer',
												flexShrink: 0,
											}}
											onClick={() =>
												handleViewDocument(
													fileKeys.puc_doc_key,
													'PUC Document'
												)
											}
										/>
									</FileInfo>
									{isEditMode && (
										<RemoveFileButton
											onClick={() => handleRemoveFile('puc_doc')}
										>
											<CloseOutlined />
										</RemoveFileButton>
									)}
								</FileDisplayContainer>
							)}
							<HiddenFileInput
								type="file"
								ref={pucDocFileInputRef}
								accept=".pdf,.png,.jpg,.jpeg"
								onChange={(e) => handleFileChange(e, 'puc_doc')}
							/>
						</UploadButtonWrapper>
						<DatePickerWrapper>
							<Form.Item
								name="puc_expiry_date"
								label="Valid Till"
								required={isPucExpiryDateRequired}
								dependencies={['puc_doc_key']}
								rules={[
									{
										validator: (_, value) => {
											const pucDocKey = form.getFieldValue('puc_doc_key');
											if (skipDocumentUpload || !pucDocKey || value) {
												return Promise.resolve();
											}

											return Promise.reject(
												new Error(VALID_TILL_DATE_MESSAGE)
											);
										},
									},
								]}
								style={{ marginBottom: 0 }}
							>
								<DatePicker
									style={{ width: '100%' }}
									placeholder="Eg : 12-2-2025"
									format="DD-MM-YYYY"
									disabled={!isEditMode || skipDocumentUpload}
								/>
							</Form.Item>
						</DatePickerWrapper>
					</InlineRow>
				</Form.Item>

				{/* Row 4: Owner Ship Type | Upload File (consent letter) */}
				<Form.Item name="ownership_type" label="Owner Ship Type">
					<Select placeholder="Select" disabled={!isEditMode || skipDocumentUpload}>
						{OWNERSHIP_TYPE_OPTIONS.map((option) => (
							<Option key={option.value} value={option.value}>
								{option.label}
							</Option>
						))}
					</Select>
				</Form.Item>
				<Form.Item
					name="consent_letter_key"
					label="Upload File"
					required={ownershipType === 'Aggregator' && !skipDocumentUpload}
					dependencies={['ownership_type']}
					rules={[
						{
							validator: (_, value) => {
								const ownershipTypeValue = form.getFieldValue('ownership_type');
								if (
									ownershipTypeValue === 'Aggregator' &&
									!value &&
									!skipDocumentUpload
								) {
									return Promise.reject(
										new Error('Please upload consent letter for Aggregator')
									);
								}
								return Promise.resolve();
							},
						},
					]}
				>
					<InlineRow>
						<Input
							placeholder="Eg : File"
							value={fileNames.consent_letter || ''}
							disabled
							readOnly
							style={{ backgroundColor: '#f9fafb', flex: 1 }}
						/>
						<PrimaryBrowseFileButton
							icon={<UploadOutlined />}
							onClick={() => handleBrowseFile('consent_letter', consentFileInputRef)}
							loading={uploading.consent_letter}
							disabled={!isEditMode || !ownershipType || skipDocumentUpload}
						>
							Browse File
						</PrimaryBrowseFileButton>
					</InlineRow>
					{fileNames.consent_letter && (
						<FileDisplayContainer style={{ marginTop: 8 }}>
							<FileInfo>
								<FileIcon />
								<Text
									style={{
										fontSize: 14,
										color: '#3b82f6',
										cursor: 'pointer',
										flex: 1,
										minWidth: 0,
									}}
									onClick={() =>
										handleViewDocument(
											fileKeys.consent_letter_key,
											'Consent Letter'
										)
									}
								>
									{fileNames.consent_letter}
								</Text>
								<EyeOutlined
									style={{
										fontSize: 16,
										color: '#3b82f6',
										cursor: 'pointer',
										flexShrink: 0,
									}}
									onClick={() =>
										handleViewDocument(
											fileKeys.consent_letter_key,
											'Consent Letter'
										)
									}
								/>
							</FileInfo>
							{isEditMode && (
								<RemoveFileButton
									onClick={() => handleRemoveFile('consent_letter')}
								>
									<CloseOutlined />
								</RemoveFileButton>
							)}
						</FileDisplayContainer>
					)}
					<HiddenFileInput
						type="file"
						ref={consentFileInputRef}
						accept=".pdf,.png,.jpg,.jpeg"
						onChange={(e) => handleFileChange(e, 'consent_letter')}
					/>
				</Form.Item>
				{/* Row 5: GPS Installed | GPS Provider Name */}
				<Form.Item
					name="gps_installed"
					label="GPS Installed"
					rules={[{ required: true, message: 'Please select GPS installation status' }]}
				>
					<Select placeholder="Select" disabled={!isEditMode}>
						<Option value>Yes</Option>
						<Option value={false}>No</Option>
					</Select>
				</Form.Item>
				{gpsInstalled === true && (
					<Form.Item
						name="gps_provider_name"
						label="GPS Provider Name"
						rules={[
							{
								required: true,
								message: 'Please enter GPS provider name',
							},
						]}
					>
						<Input placeholder="Enter Provider Name" disabled={!isEditMode} />
					</Form.Item>
				)}
			</FormGrid>

			{isEditMode && (
				<FormActions>
					<Button onClick={handleToggleEdit} size="large">
						Cancel
					</Button>
					<ActionButton
						type="primary"
						onClick={handleSubmit}
						loading={loading}
						size="large"
					>
						Save Changes
					</ActionButton>
				</FormActions>
			)}
		</StyledForm>
	);
}

VehicleDetailsForm.propTypes = {
	form: PropTypes.object.isRequired,
	isEditMode: PropTypes.bool.isRequired,
	loading: PropTypes.bool.isRequired,
	ownershipType: PropTypes.string,
	vendorOptions: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.number.isRequired,
			label: PropTypes.string.isRequired,
		})
	).isRequired,
	vehicleTypeOptions: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.number.isRequired,
			label: PropTypes.string.isRequired,
		})
	).isRequired,
	uploading: PropTypes.shape({
		rc_doc: PropTypes.bool,
		insurance: PropTypes.bool,
		puc_doc: PropTypes.bool,
		consent_letter: PropTypes.bool,
	}).isRequired,
	fileKeys: PropTypes.shape({
		rc_doc_key: PropTypes.string,
		insurance_key: PropTypes.string,
		puc_doc_key: PropTypes.string,
		consent_letter_key: PropTypes.string,
	}).isRequired,
	fileNames: PropTypes.shape({
		rc_doc: PropTypes.string,
		insurance: PropTypes.string,
		puc_doc: PropTypes.string,
		consent_letter: PropTypes.string,
	}).isRequired,
	rcDocFileInputRef: PropTypes.object.isRequired,
	insuranceDocFileInputRef: PropTypes.object.isRequired,
	pucDocFileInputRef: PropTypes.object.isRequired,
	consentFileInputRef: PropTypes.object.isRequired,
	handleBrowseFile: PropTypes.func.isRequired,
	handleFileChange: PropTypes.func.isRequired,
	handleViewDocument: PropTypes.func.isRequired,
	handleRemoveFile: PropTypes.func.isRequired,
	handleToggleEdit: PropTypes.func.isRequired,
	handleSubmit: PropTypes.func.isRequired,
	skipDocumentUpload: PropTypes.bool.isRequired,
};

VehicleDetailsForm.defaultProps = {
	ownershipType: null,
};

export default VehicleDetailsForm;
